/**
 * The seam between a Muse headset transport and the signal pipeline.
 *
 * Every source — Web Bluetooth, the Myndchime app's local WebSocket bridge, or
 * the simulator — hands the pipeline the same blocks: native-rate samples with
 * an acquisition time for the first one, and how many samples went missing
 * before it. Nothing downstream needs to know which one is connected.
 */

/** Sources exposed by the production device manager. */
export type DeviceSourceKind = "bluetooth" | "bridge";
/** The simulator remains available to synthetic transport tests and preview fixtures. */
export type SourceKind = DeviceSourceKind | "simulator";

export function isDeviceSourceKind(value: unknown): value is DeviceSourceKind {
  return value === "bluetooth" || value === "bridge";
}

/** Muse device electrode order on the wire: two temporal-parietal, two frontal. */
export const EEG_SITES = ["TP9", "AF7", "AF8", "TP10"] as const;
export type EegSite = typeof EEG_SITES[number];

/** Muse device optics4 preset (p1035): inner left/right at 730 nm (NIR) and 850 nm (IR). */
export const OPTICS_CHANNELS = ["LI_NIR", "RI_NIR", "LI_IR", "RI_IR"] as const;
export type OpticsChannel = typeof OPTICS_CHANNELS[number];

/**
 * Original Muse PPG wire order (SDK AMBIENT/IR/RED). Muse S repurposes the
 * first channel as green or another IR signal, so labels remain model-neutral.
 * These are not fNIRS spatial channels.
 */
export const LEGACY_PPG_CHANNELS = ["PPG_1", "PPG_2", "PPG_3"] as const;

export const EEG_RATE = 256;
export const OPTICS_RATE = 64;

export type EegBlock = {
  /** Epoch seconds of samples[*][0]. */
  t0: number;
  rate: typeof EEG_RATE;
  /** One array per EEG_SITES entry, µV. NaN marks a sample the source did not deliver. */
  samples: Float32Array[];
  /** Estimated samples lost immediately before this block; 0 when continuous. */
  gapBefore: number;
};

export type OpticsBlock = {
  t0: number;
  rate: typeof OPTICS_RATE;
  /** One array per OPTICS_CHANNELS entry, raw intensity (arbitrary units). */
  samples: Float32Array[];
  gapBefore: number;
};

export type PpgBlock = {
  t0: number;
  rate: typeof OPTICS_RATE;
  /** One array per LEGACY_PPG_CHANNELS entry, raw unsigned 24-bit intensity. */
  samples: Float32Array[];
  gapBefore: number;
};

export type SourcePhase = "idle" | "requesting" | "connecting" | "configuring" | "streaming" | "halted" | "reconnecting" | "error" | "closed";

export type SourceStatus = { phase: SourcePhase; detail: string; deviceName: string | null };

export type SourceDiagnostics = {
  packets: number;
  lostPackets: number;
  unknownTags: number;
  parseErrors: number;
  lastPacketAt: number | null;
  /** Successfully decoded original Muse PPG characteristic notifications. */
  ppgPackets?: number;
  lastPpgPacketAt?: number | null;
};

export interface SourceSink {
  eeg(block: EegBlock): void;
  optics(block: OpticsBlock): void;
  ppg?(block: PpgBlock): void;
  battery(percent: number): void;
  status(status: SourceStatus): void;
  diagnostics(patch: Partial<SourceDiagnostics>): void;
}

export interface MuseSource {
  readonly deviceKind: "muse";
  readonly kind: SourceKind;
  readonly label: string;
  readonly capabilities: { optics: boolean; battery: boolean; ppg?: boolean };
  /**
   * Connect and start streaming. For Web Bluetooth the browser's chooser needs
   * the click's user activation, so callers must not await anything first.
   */
  connect(sink: SourceSink): Promise<void>;
  /** Pause acquisition while keeping the link (Muse device: `h`). */
  halt(): Promise<void>;
  resume(): Promise<void>;
  disconnect(): Promise<void>;
}

export const SOURCE_LABELS: Record<SourceKind, string> = {
  bluetooth: "Bluetooth",
  bridge: "Local app bridge",
  simulator: "Simulated device",
};

export function emptyDiagnostics(): SourceDiagnostics {
  return { packets: 0, lostPackets: 0, unknownTags: 0, parseErrors: 0, lastPacketAt: null };
}
